import { useQuery } from "@tanstack/react-query";
import { fetcher } from "../utils/api";
import { buildImageUrl } from "../utils/imageUtils";
import { QUERY_KEYS } from "./usePrefetchStoreData";

type Subscription = {
  id: number;
  name: string;
  image?: string;
  category: number;
  prices: {
    id: number;
    size: "motorcycle" | "small" | "medium" | "large" | "extra_large";
    final_price: number;
  }[];
};

type PaginatedSubscriptionResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Subscription[];
};

export function useSubscriptions(page: number = 1) {
  return useQuery<PaginatedSubscriptionResponse>({
    queryKey: ["subscriptions", page],
    queryFn: async () => {
      const data = await fetcher<PaginatedSubscriptionResponse>(
        `/store/subscriptions/?page=${page}&page_limit=100`
      );

      const subscriptionsWithFixedImages = data.results.map((subscription) => ({
        ...subscription,
        image: (buildImageUrl(subscription.image ?? null) ?? undefined) as
          | string
          | undefined,
      }));

      return {
        ...data,
        results: subscriptionsWithFixedImages,
      };
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000, 
  });
}


// للاستخدام في أماكن أخرى بدون pagination - يجلب جميع الصفحات
export function useAllSubscriptions() {
  return useQuery<Subscription[]>({
    queryKey: QUERY_KEYS.subscriptionsAll,
    queryFn: async () => {
      const allSubscriptions: Subscription[] = [];
      let nextUrl: string | null = "/store/subscriptions/?page_limit=100";

      // جلب جميع الصفحات
      while (nextUrl) {
        const data: PaginatedSubscriptionResponse = await fetcher<PaginatedSubscriptionResponse>(nextUrl);
        allSubscriptions.push(...data.results);

        // استخراج الـ path من next URL إذا كان موجوداً
        if (data.next) {
          const url: URL = new URL(data.next);
          nextUrl = url.pathname + url.search;
        } else {
          nextUrl = null;
        }
      }

      const subscriptionsWithFixedImages = allSubscriptions.map((subscription) => ({
        ...subscription,
        image: (buildImageUrl(subscription.image ?? null) ?? undefined) as
          | string
          | undefined,
      }));

      return subscriptionsWithFixedImages;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useSubscription(id: number | string | null | undefined) {
  return useQuery<Subscription>({
    queryKey: ["subscriptions", id],
    queryFn: async () => {
      const subscription = await fetcher<Subscription>(
        `/store/subscriptions/${id}/`
      );

      // معالجة URL الصورة
      return {
        ...subscription,
        image: (buildImageUrl(subscription.image ?? null) ?? undefined) as
          | string
          | undefined,
      };
    },
    enabled: !!id && id !== 0 && id !== "0",
  });
}
